import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { colorPresets, defaultThemePreset, getAppTheme } from '@/theme';
import type { ThemeMode, ThemePreset } from '@/types';

const MODE_STORAGE_KEY = 'lm_theme_mode';
const PRESET_STORAGE_KEY = 'lm_theme_preset';
const SIDEBAR_COLOR_STORAGE_KEY = 'lm_sidebar_color';
const CUSTOM_COLORS_STORAGE_KEY = 'lm_custom_colors';
const FORM_CARD_LIGHT_MODE_KEY = 'lm_form_card_light_mode';

interface CustomColors {
  primaryHue: number;
  secondaryHue: number;
  useGradient: boolean;
}

interface ThemeContextValue {
  mode: ThemeMode;
  preset: ThemePreset;
  sidebarColor?: string;
  customColors: CustomColors;
  formCardLightMode: boolean;
  presets: Array<{ value: ThemePreset; label: string }>;
  setMode: (mode: ThemeMode) => void;
  setPreset: (preset: ThemePreset) => void;
  setSidebarColor: (color: string) => void;
  setCustomColors: (colors: CustomColors) => void;
  setFormCardLightMode: (val: boolean) => void;
  toggleTheme: () => void;
}

const ThemeCtx = createContext<ThemeContextValue>({
  mode: 'light',
  preset: defaultThemePreset,
  presets: [],
  customColors: { primaryHue: 0, secondaryHue: 180, useGradient: true },
  formCardLightMode: true,
  setMode: () => undefined,
  setPreset: () => undefined,
  setSidebarColor: () => undefined,
  setCustomColors: () => undefined,
  setFormCardLightMode: () => undefined,
  toggleTheme: () => undefined,
});

function getInitialMode(): ThemeMode {
  const stored = localStorage.getItem(MODE_STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getInitialPreset(): ThemePreset {
  const stored = localStorage.getItem(PRESET_STORAGE_KEY);
  if (stored && stored in colorPresets) return stored as ThemePreset;
  return defaultThemePreset;
}

export function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);
  const [preset, setPreset] = useState<ThemePreset>(getInitialPreset);
  const [sidebarColor, setSidebarColor] = useState<string | undefined>(() => {
    return localStorage.getItem(SIDEBAR_COLOR_STORAGE_KEY) || undefined;
  });
  const [customColors, setCustomColors] = useState<CustomColors>(() => {
    try {
      const stored = localStorage.getItem(CUSTOM_COLORS_STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch { /* ... */ }
    return { primaryHue: 0, secondaryHue: 180, useGradient: true };
  });
  const [formCardLightMode, setFormCardLightMode] = useState<boolean>(() => {
    return localStorage.getItem(FORM_CARD_LIGHT_MODE_KEY) !== 'false';
  });

  useEffect(() => {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem(PRESET_STORAGE_KEY, preset);
  }, [preset]);

  useEffect(() => {
    if (sidebarColor) {
      localStorage.setItem(SIDEBAR_COLOR_STORAGE_KEY, sidebarColor);
    } else {
      localStorage.removeItem(SIDEBAR_COLOR_STORAGE_KEY);
    }
  }, [sidebarColor]);

  useEffect(() => {
    localStorage.setItem(CUSTOM_COLORS_STORAGE_KEY, JSON.stringify(customColors));
  }, [customColors]);
  
  useEffect(() => {
    localStorage.setItem(FORM_CARD_LIGHT_MODE_KEY, String(formCardLightMode));
  }, [formCardLightMode]);

  const toggleTheme = () => setMode((prev) => (prev === 'light' ? 'dark' : 'light'));

  const theme = useMemo(() => {
    if (preset === 'custom') {
      const h1 = customColors.primaryHue;
      const h2 = customColors.useGradient ? customColors.secondaryHue : customColors.primaryHue;
      return getAppTheme(mode, preset, {
        primary:   `hsl(${h1}, 78%, 56%)`,
        secondary: `hsl(${h2}, 70%, 54%)`,
        accent:    `hsl(${(h2 + 120) % 360}, 82%, 58%)`,
      });
    }
    return getAppTheme(mode, preset);
  }, [mode, preset, customColors]);

  const presets = useMemo(
    () => Object.entries(colorPresets).map(([value, item]) => ({ value: value as ThemePreset, label: item.label })),
    [],
  );

  const value = useMemo(
    () => ({ mode, preset, presets, sidebarColor, customColors, formCardLightMode, setMode, setPreset, setSidebarColor, setCustomColors, setFormCardLightMode, toggleTheme }),
    [mode, preset, presets, sidebarColor, customColors, formCardLightMode],
  );

  return (
    <ThemeCtx.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeCtx.Provider>
  );
}

export function useThemeMode(): ThemeContextValue {
  return useContext(ThemeCtx);
}

